// =====================================================
// APOLLO GRAPHQL CLIENT CONFIGURATION
// Complete GraphQL client setup with caching, error handling, and WebSocket support
// =====================================================

import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  split,
  from,
  Observable,
  ServerError,
  ServerParseError,
} from '@apollo/client';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { getGraphQLWebSocketURL } from '@/config/websocket';
import { TokenStorage } from '@/contexts';

// =====================================================
// CLIENT CONFIGURATION
// =====================================================

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787';
const WS_URL = getGraphQLWebSocketURL();

// =====================================================
// HTTP LINK WITH AUTHENTICATION
// =====================================================

const httpLink = new HttpLink({
  uri: `${API_URL}/graphql`,
  credentials: 'include',
});

// =====================================================
// WEBSOCKET LINK FOR SUBSCRIPTIONS
// =====================================================

const wsClient = createClient({
  url: `${WS_URL}/graphql`,
  connectionParams: () => {
    const token = TokenStorage.getAccessToken();
    return {
      authorization: token ? `Bearer ${token}` : '',
    };
  },
  retryAttempts: 5,
  shouldRetry: () => true,
  on: {
    connected: () => console.log('[GraphQL WS] Connected'),
    closed: () => console.log('[GraphQL WS] Disconnected'),
    error: (err) => console.error('[GraphQL WS] Error:', err),
  },
});

const wsLink = new GraphQLWsLink(wsClient);

// =====================================================
// AUTHENTICATION MIDDLEWARE
// =====================================================

const authMiddleware = new ApolloLink((operation, forward) => {
  const token = TokenStorage.getAccessToken();

  operation.setContext(({ headers = {} }) => ({
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
      'x-tenant-id': localStorage.getItem('tenant_id') || '',
    },
  }));

  return forward(operation);
});

// =====================================================
// ERROR HANDLING LINK
// =====================================================

const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      console.error(
        `[GraphQL Error]: Message: ${err.message}, Location: ${JSON.stringify(
          err.locations
        )}, Path: ${err.path}`
      );

      // Handle authentication errors
      if (err.extensions?.code === 'UNAUTHENTICATED') {
        // Try to refresh token using TokenStorage
        const refreshToken = TokenStorage.getRefreshToken();

        if (refreshToken) {
          return new Observable((observer) => {
            refreshAuthToken(refreshToken)
              .then((newToken) => {
                if (newToken) {
                  TokenStorage.setAccessToken(newToken, 3600); // Default 1 hour
                  TokenStorage.setLegacyToken(newToken); // For backward compatibility

                  // Retry the operation with new token
                  operation.setContext(({ headers = {} }) => ({
                    headers: {
                      ...headers,
                      authorization: `Bearer ${newToken}`,
                    },
                  }));

                  const subscriber = {
                    next: observer.next.bind(observer),
                    error: observer.error.bind(observer),
                    complete: observer.complete.bind(observer),
                  };

                  forward(operation).subscribe(subscriber);
                } else {
                  handleAuthError();
                  observer.error(err);
                }
              })
              .catch((refreshErr) => {
                handleAuthError();
                observer.error(refreshErr);
              });
          });
        } else {
          handleAuthError();
        }
      }

      // Handle rate limiting
      if (err.extensions?.code === 'RATE_LIMITED') {
        console.warn('[GraphQL] Rate limited, implementing exponential backoff');
      }
    }
  }

  if (networkError) {
    console.error(`[Network Error]: ${networkError.message}`);

    // Handle offline scenarios
    if (!navigator.onLine) {
      console.warn('[Network] Device is offline');
      // Implement offline queue if needed
    }
  }
});

// =====================================================
// RETRY LINK FOR NETWORK FAILURES
// =====================================================

const retryLink = new RetryLink({
  delay: {
    initial: 300,
    max: 5000,
    jitter: true,
  },
  attempts: {
    max: 3,
    retryIf: (error, _operation) => {
      // Retry on network errors but not on GraphQL errors
      return !!error && !error.result;
    },
  },
});

// =====================================================
// SPLIT LINK FOR HTTP/WS
// =====================================================

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    );
  },
  wsLink,
  from([authMiddleware, errorLink, retryLink, httpLink])
);

// =====================================================
// CACHE CONFIGURATION
// =====================================================

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        features: {
          keyArgs: ['tenantId', 'input', ['featureType', 'status', 'query']],
          merge(existing, incoming, { args }) {
            // Pagination merge logic
            if (!existing) return incoming;

            const { page = 1 } = args?.input || {};

            if (page === 1) {
              return incoming;
            }

            return {
              ...incoming,
              items: [...(existing.items || []), ...incoming.items],
            };
          },
        },
        executions: {
          keyArgs: ['featureId', 'tenantId', 'status'],
          merge(existing, incoming, { args }) {
            if (!existing) return incoming;

            const { page = 1 } = args?.pagination || {};

            if (page === 1) {
              return incoming;
            }

            return {
              ...incoming,
              items: [...(existing.items || []), ...incoming.items],
            };
          },
        },
      },
    },
    Feature: {
      keyFields: ['id'],
      fields: {
        executions: {
          merge(existing = [], incoming) {
            return [...incoming];
          },
        },
        deployments: {
          merge(existing = [], incoming) {
            return [...incoming];
          },
        },
      },
    },
    Agent: {
      keyFields: ['id'],
    },
    Tenant: {
      keyFields: ['id'],
    },
    User: {
      keyFields: ['id'],
    },
    Deployment: {
      keyFields: ['id'],
    },
    FeatureExecution: {
      keyFields: ['id'],
    },
  },
});

// =====================================================
// APOLLO CLIENT INSTANCE
// =====================================================

export const apolloClient = new ApolloClient({
  link: splitLink,
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  connectToDevTools: import.meta.env.DEV,
});

// =====================================================
// UTILITY FUNCTIONS
// =====================================================

async function refreshAuthToken(refreshToken: string): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/graphql`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          mutation RefreshToken($refreshToken: String!) {
            refreshToken(refreshToken: $refreshToken) {
              accessToken
              expiresIn
            }
          }
        `,
        variables: { refreshToken },
      }),
    });

    const { data } = await response.json();
    return data?.refreshToken?.accessToken || null;
  } catch (error) {
    console.error('[Auth] Token refresh failed:', error);
    return null;
  }
}

function handleAuthError() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('tenant_id');

  // Redirect to login
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
}

// =====================================================
// CACHE UTILITIES
// =====================================================

export const clearCache = () => {
  apolloClient.cache.reset();
};

export const evictCache = (typename: string, id: string) => {
  apolloClient.cache.evict({ id: `${typename}:${id}` });
  apolloClient.cache.gc();
};

// =====================================================
// EXPORT CLIENT
// =====================================================

export default apolloClient;
